import {
  AbilityBuilder,
  createMongoAbility,
  type MongoAbility,
} from "@casl/ability";
import type { AppPermission } from "./auth.schema.ts";
import type { PermissionRule } from "./rbac.schema.ts";

export type AppAbilityAction = "read" | "use" | "manage";
// "all" is a CASL sentinel meaning "every subject"; never stored in DB permissions.
export type AppAbilitySubject = AppPermission | "all";

export type AppAbility = MongoAbility<[AppAbilityAction, AppAbilitySubject]>;

export { AbilityBuilder, createMongoAbility };

export function buildAbility(rules: PermissionRule[]): AppAbility {
  const { can, cannot, build } = new AbilityBuilder<AppAbility>(
    createMongoAbility,
  );
  for (const rule of rules) {
    const subject = rule.subject as AppAbilitySubject;
    if (rule.inverted) {
      cannot(rule.action, subject);
      // Revoke implied actions too so cannot("manage") also revokes use+read
      if (rule.action === "manage") {
        cannot("use", subject);
        cannot("read", subject);
      } else if (rule.action === "use") {
        cannot("read", subject);
      }
    } else {
      const cond = rule.conditions as Parameters<typeof can>[2] | undefined;
      if (cond) {
        can(rule.action, subject, cond);
        // Expand action hierarchy — conditions apply only to the primary action;
        // implied read has no conditions so readers can still check visibility.
        if (rule.action === "manage") {
          can("use", subject, cond);
          can("read", subject);
        } else if (rule.action === "use") {
          can("read", subject);
        }
      } else {
        can(rule.action, subject);
        if (rule.action === "manage") {
          can("use", subject);
          can("read", subject);
        } else if (rule.action === "use") {
          can("read", subject);
        }
      }
    }
  }
  return build();
}

/**
 * Reads the axis-2 `documentTypeId` condition from a rule's conditions,
 * whether it sits at the top level (`{ documentTypeId }`) or nested inside the
 * axis-1 `$and` wrapper that `buildAbility` synthesizes for customer-owned
 * subjects (BEL-530: `{ $and: [{ customerId }, { documentTypeId }] }`).
 *
 * `found: false` means the rule carries no documentTypeId restriction at all —
 * unrestricted across document types (still scoped by axis-1 customerId).
 */
function readDocumentTypeCondition(
  cond: Record<string, unknown> | null | undefined,
): { found: boolean; value?: unknown } {
  if (!cond) return { found: false };
  if (cond["documentTypeId"] !== undefined)
    return { found: true, value: cond["documentTypeId"] };
  const and = cond["$and"];
  if (Array.isArray(and)) {
    for (const clause of and) {
      if (
        clause &&
        typeof clause === "object" &&
        "documentTypeId" in (clause as Record<string, unknown>)
      )
        return {
          found: true,
          value: (clause as Record<string, unknown>)["documentTypeId"],
        };
    }
  }
  return { found: false };
}

/**
 * CASL cannot evaluate conditions against plain string subjects via ability.can().
 * This helper manually checks whether a user has `use` access to `requests.write`
 * for a specific documentTypeId by inspecting the ability's raw rules.
 *
 * Returns true if:
 * - There is a `manage/all` rule (super-admin), OR
 * - There is a `use/requests.write` rule with no documentTypeId condition (unrestricted), OR
 * - There is a `use/requests.write` rule whose documentTypeId condition === documentTypeId
 *   (read from the top level OR from inside the axis-1 `$and` wrapper — BEL-530).
 */
export function canUseRequests(
  ability: AppAbility,
  documentTypeId: string,
): boolean {
  for (const r of ability.rules) {
    if (r.inverted) continue;
    const action = r.action as string;
    const subject = r.subject as string;
    if (action === "manage" && subject === "all") return true;
    if (
      (action === "use" || action === "manage") &&
      subject === "requests.write"
    ) {
      const cond = r.conditions as Record<string, unknown> | null | undefined;
      const docType = readDocumentTypeCondition(cond);
      if (!docType.found) return true;
      if (docType.value === documentTypeId) return true;
    }
  }
  return false;
}

export function buildAbilityFromLegacy(
  permissions: AppPermission[],
  isSuperAdmin: boolean,
): AppAbility {
  const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility);
  if (isSuperAdmin) {
    can("manage", "all");
    can("use", "all");
    can("read", "all");
  } else {
    for (const perm of permissions) {
      can("use", perm);
      can("read", perm);
    }
  }
  return build();
}
